import { MaskReveal, FadeUp, ParallaxImage } from "./Reveal";

const OPENING = [
  "For much of my life, I searched for answers and healing outside of myself.",
  "Like many people, I believed they were waiting somewhere else—another place, another teacher, another experience, another person, another book.",
];

const MIDDLE = [
  "Over the years, my journey took me through different countries, cultures, and traditions. I explored yoga, meditation, conscious movement, breathwork, and spiritual practices across Asia and beyond, learning from remarkable teachers and transformative experiences.",
  "Each one offered something valuable.",
  "But none of them gave me the answers I was searching for.",
  "Those answers began to reveal themselves only when I slowed down, breathed deeply, and started listening to my own body.",
  "Through conscious movement and breath, I discovered that the body carries far more than muscles and bones. It holds tension, emotions, memories, and stories waiting to be heard.",
  "As I learned to listen instead of resist, I realized that transformation wasn't about becoming someone new—it was about remembering who I had always been.",
];

const STANZA = [
  "A place where we slow down.",
  "Where movement becomes a conversation with the body.",
  "Where breath creates space for presence.",
  "Where we soften.",
  "And where we remember who we are—a gentle journey back home to ourselves.",
];

const Body = ({ children, muted = false }) => (
  <p className={`font-serif text-lg font-light leading-relaxed md:text-xl ${muted ? "text-gaia-charcoal/55 italic" : "text-gaia-charcoal/80"}`}>
    {children}
  </p>
);

const About = () => {
  return (
    <section id="about" className="bg-gaia-bg py-20 md:py-28" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <FadeUp>
          <span className="font-sans text-[11px] uppercase tracking-[0.32em] text-gaia-olive">Our Journey</span>
        </FadeUp>
        <MaskReveal
          as="h2"
          lines={["Why Gaia Space Exists"]}
          className="mt-4 font-serif text-4xl font-light leading-[1.04] tracking-tight text-gaia-charcoal md:text-6xl"
          data-testid="about-headline"
        />

        <div className="mt-12 grid grid-cols-1 gap-10 md:mt-16 md:grid-cols-12 md:gap-16">
          {/* Left: sticky portrait + the stanza as a visual poem */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <ParallaxImage
                src="/angela-why.png"
                alt="Angela in quiet meditation, hands at heart, beneath a palm"
                className="aspect-[4/5] w-full"
                distance={34}
                data-testid="about-image"
              />
              <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.28em] text-gaia-taupe">
                Angela · Southern Spain
              </p>
              <FadeUp delay={0.1}>
                <div
                  className="mt-10 space-y-2 border-l border-gaia-terracotta/40 pl-6 font-serif text-xl font-light italic leading-relaxed text-gaia-charcoal/70 md:text-2xl"
                  data-testid="about-stanza"
                >
                  {STANZA.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>

          {/* Right: the letter */}
          <div className="md:col-span-6 md:col-start-7">
            <div className="space-y-5">
              {OPENING.map((p, i) => (
                <FadeUp key={`o-${i}`} delay={i * 0.04}><Body>{p}</Body></FadeUp>
              ))}
            </div>

            <FadeUp><div className="mt-5"><Body muted>Life, however, had a different plan.</Body></div></FadeUp>

            <div className="mt-5 space-y-5">
              {MIDDLE.map((p, i) => (
                <FadeUp key={`m-${i}`} delay={i * 0.03}><Body>{p}</Body></FadeUp>
              ))}
            </div>

            <FadeUp>
              <p
                className="my-12 border-y border-gaia-terracotta/25 py-8 font-serif text-2xl font-light italic leading-snug text-gaia-terracotta md:text-4xl"
                data-testid="about-highlight"
              >
                Gaia Space is the natural expression of that journey.
              </p>
            </FadeUp>

            <div className="space-y-5">
              <FadeUp><Body>I don't believe people need to be fixed.</Body></FadeUp>
              <FadeUp delay={0.04}><Body>I believe we all need spaces that help us remember who we are.</Body></FadeUp>
            </div>

            <FadeUp delay={0.06}>
              <p className="mt-10 font-serif text-3xl font-light italic text-gaia-charcoal md:text-4xl" data-testid="about-welcome">
                Welcome to Gaia Space.
              </p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
